import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { FaEye, FaEyeSlash, FaLock } from "react-icons/fa";
import { FiMail } from "react-icons/fi";

// Import des hooks et constantes de Thème
import { useTheme } from "../contexts/ThemeContext";
import { THEMES, THEME_COLORS, getLogoPath } from "../components/Constants";

import { authFetch } from "../utils/api";

// Import de TOUS les logos column
import LogoColumnBlue from '../images/logo_column_blue.png';
import LogoColumnGreen from '../images/logo_column_green.png';
import LogoColumnRed from '../images/logo_column_red.png';
import LogoColumnOrange from '../images/logo_column_orange.png';
import LogoColumnYellow from '../images/logo_column_yellow.png';

// Mapping pour le chargement dynamique du logo
const LOGO_COLUMN_IMAGES = {
  'logo_column_blue.png': LogoColumnBlue,
  'logo_column_green.png': LogoColumnGreen,
  'logo_column_red.png': LogoColumnRed,
  'logo_column_orange.png': LogoColumnOrange,
  'logo_column_yellow.png': LogoColumnYellow,
};

export default function LoginPage({ onLogin }) {

  //const [searchParams] = useSearchParams(); 
  //const [verificationMessage, setVerificationMessage] = useState("");
  const { currentTheme } = useTheme();
  const themeColors = THEME_COLORS[currentTheme] || THEME_COLORS[THEMES.BLUE];

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  

  // Logique de sélection dynamique du logo
  const logoFileName = getLogoPath(currentTheme, 'column');
  const LogoImage = LOGO_COLUMN_IMAGES[logoFileName];

  const handleLogin = async (e) => {
      e.preventDefault();
      try {
        const response = await authFetch.post("/login", { email, password });
        const data = await response.json();

        if (!response.ok) {
          setError(data.error || "Erreur lors de la connexion.");
          return;
        }

        const { user } = data;

        if (user) {
          localStorage.setItem('user', JSON.stringify(user));
          setError("");
          onLogin(user); 

          // 💡 REDIRECTION CONDITIONNELLE ICI
          if (user.role === 'Admin') {
              navigate(`/admin-${user.adminPath}`);
          } else {
              navigate("/comptes");
          }
        } else {
          setError("Réponse du serveur incomplète.");
        }
      } catch (err) {
        console.error("❌ Erreur réseau :", err);
        setError("Impossible de contacter le serveur.");
      }
  };

  return (
    <div style={styles.container}>
      <div style={{ ...styles.card, color: themeColors.text }}>
        <div style={styles.logoContainer}>
          <img
            src={LogoImage} // <-- Logo dynamique
            alt="Mon Budget App Logo"
            style={styles.logoImage}
          />
        </div>
        <h2 style={styles.title}>Connexion</h2>
        <form onSubmit={handleLogin}>
          <div style={styles.inputGroup}>
            <div style={styles.inputWrapper}>
              <FiMail style={{ ...styles.inputIcon, color: themeColors.secondary }} />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{ ...styles.input, border: `1px solid ${themeColors.inputBorder}` }}
                placeholder="E-mail"
                required
              />
            </div>
          </div>
          <div style={styles.inputGroup}>
            <div style={styles.passwordWrapper}>
              <FaLock style={{ ...styles.inputIcon, color: themeColors.secondary }} />
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{ ...styles.input, border: `1px solid ${themeColors.inputBorder}` }}
                placeholder="Mot de passe"
                required
              />
              <span
                onClick={() => setShowPassword(!showPassword)}
                style={{ ...styles.eyeIcon, color: themeColors.secondary }}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>
          </div>
          {error && <p style={{ color: themeColors.danger, textAlign: 'center', fontWeight: 'bold' }}>{error}</p>}
          <p style={styles.forgotPasswordLink}>
              <Link to="/forgot-password" style={{ ...styles.link, color: themeColors.secondary, fontWeight: 'normal' }}>
                  Mot de passe oublié ?
              </Link>
          </p>
          <button
            type="submit"
            // Couleurs dynamiques
            style={{ ...styles.button, backgroundColor: themeColors.secondary }}
          >
            Se connecter
          </button>
        </form>
        <p style={{ textAlign: "center", marginTop: 15, color: themeColors.text }}>
          Pas encore de compte ? <Link to="/inscription" style={{ ...styles.link, color: themeColors.secondary }}>S'inscrire</Link>
        </p>
        <p style={{ textAlign: "center", marginTop: 15, paddingTop: 15, color: themeColors.text }}>
            <Link to="/" style={{...styles.link, color: themeColors.secondary}}>Retour à la page d'accueil</Link>
        </p>
      </div>
    </div>
  );
}

// Les styles de base restent inchangés
const styles = {
  container: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
    padding: "0 15px",
    boxSizing: "border-box",
  },
  card: {
    // Le background est géré dynamiquement
    padding: 30,
    borderRadius: 20,
    boxShadow: "0 4px 15px rgba(0,0,0,0.15)",
    width: "100%",
    maxWidth: 400,
    boxSizing: "border-box",
    backgroundColor: '#f9f9f9',
    // La couleur du texte est gérée dynamiquement
  },
  title: {
    textAlign: "center",
    marginBottom: 20,
    display: 'none',
  },
  inputGroup: {
    marginBottom: 15,
  },
  input: {
    width: "100%",
    padding: "12px 12px 12px 40px",
    fontSize: 16,
    borderRadius: 10,
    // La bordure est gérée dynamiquement
    boxSizing: "border-box",
    transition: 'border-color 0.2s',
    // Les styles :focus/&amp;:hover ne peuvent pas être appliqués ici avec les styles inline.
    // Nous devrons vivre avec cette limitation ou passer par des CSS modules si le besoin est critique.
    outline: 'none', // Retirer l'outline par défaut
  },
  passwordWrapper: {
    position: "relative",
    width: "100%",
  },
  eyeIcon: {
    position: "absolute",
    right: 10,
    top: "50%",
    transform: "translateY(-50%)",
    cursor: "pointer",
    // La couleur est gérée dynamiquement
    zIndex: 2,
    transition: 'color 0.2s',
  },
  button: {
    width: "100%",
    padding: 12,
    fontSize: 16,
    // La couleur de fond est gérée dynamiquement
    color: "#fff",
    border: "none",
    borderRadius: 10,
    cursor: "pointer",
    fontWeight: 'bold',
    transition: 'background-color 0.2s',
    // Les styles :hover sont difficiles à appliquer ici
  },
  logoContainer: {
    textAlign: "center",
    marginBottom: 25,
  },
  logoImage: {
    maxWidth: "100%",
    height: "auto",
    borderRadius: "20px",
  },
  inputWrapper: {
    position: "relative",
    width: "100%",
  },
  inputIcon: {
    position: 'absolute',
    left: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    // La couleur est gérée dynamiquement
    fontSize: '18px',
    zIndex: 1,
  },
  link: {
    // La couleur est gérée dynamiquement
    textDecoration: 'none',
    fontWeight: 'bold',
    transition: 'color 0.2s',
    // Les styles :hover sont difficiles à appliquer ici
  },
  forgotPasswordLink: {
    textAlign: 'right',
    marginTop: '-10px',
    marginBottom: '15px',
    fontSize: '0.9em',
  },
};